import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router";
import { ICookieConsent, ICookieConsentDialogStrings } from "..";
import CookieConsentDialog from "../Components/CookieConsent/CookieConsentDialog";
import { googleConsentMode, trackCookieConsentData } from "../Components/CookieConsent/googleConsentMode";
import {
  Action,
  sendMessageToReactNative,
  logger,
  initReactNativeBridgeListener,
  removeReactNativeBridgeListener,
  Event,
} from "../Helpers/helpers";
import { CookieConsentSettingsModel } from "./CookieConsentApp";
import { CookieConsentStringContext } from "./CookieConsentStringContext";

declare global {
  interface Window {
    dataLayer: any;
  }
}

interface Props {
  settings: CookieConsentSettingsModel;
}

const CookieConsentDialogWrapper: React.FC<Props> = (props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [consent, setConsent] = useState<ICookieConsent>();
  const cookieStrings = React.useContext<ICookieConsentDialogStrings>(CookieConsentStringContext);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [forceShow, setForceShow] = useState<boolean>(() => params.has("forceCookies"));

  const settings = props.settings;

  const hideInitialCookieConsentDialog =
    settings.isApp ||
    params.get("previewId") != null ||
    params.get("skipCookieConsent") != null ||
    pathname.indexOf("umbraco/preview") > -1;

  const requestConsentData =
    params.get("previewId") == null &&
    params.get("skipCookieConsent") == null &&
    pathname.indexOf("umbraco/preview") == -1;

  const requestConsentSettings = () => {
    const message = {
      action: Action.GetCookieConsentRequest,
      data: {},
    };
    sendMessageToReactNative(message);
  };

  const storeCookieConsentInApp = (cookieConsent: ICookieConsent) => {
    const message = {
      action: Action.SetCookieConsentRequest,
      data: { cookieConsent: cookieConsent },
    };
    sendMessageToReactNative(message);
  };

  const bridgeListener = (message: any): void => {
    try {
      const event: Event = JSON.parse(message.detail);
      if (event.action == Action.GetCookieConsentResponse) {
        const data: ICookieConsent = event.data.cookieConsent;
        if (data) {
          setConsent(data);
          if (data.hasConsented) {
            approveConsent(data.wantsAnalysis, data.wantsMarketing, data.wantsPersonalization, false);
          }
        } else {
          setForceShow(true);
          setOpen(true);
        }
      }
    } catch (error) {
      logger.logError(error);
    }
  };

  useEffect(() => {
    if (requestConsentData) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch(`/c/api/v1/kj3ks/getcmsinfo`)
        .then((x) => x.json())
        .then((x) => {
          setConsent(x.consent);
          setOpen(!x?.consent?.hasConsented || forceShow);
        });
    }
    if (settings.isApp) {
      initReactNativeBridgeListener("COOKIE_CONSENT", bridgeListener);
      requestConsentSettings();
      return () => {
        removeReactNativeBridgeListener("COOKIE_CONSENT", bridgeListener);
      };
    }
  }, []);

  const approveConsent = (
    analysis: boolean,
    marketing: boolean,
    personalization: boolean,
    saveCookieConsentInApp: boolean = true,
  ) => {
    const hasConsentedBefore = consent?.hasConsented === true;
    const wantsPersonalization = settings.disableCookieConsentForMarketingAndPersonalisation ? false : personalization;
    const wantsMarketing = settings.disableCookieConsentForMarketingAndPersonalisation ? false : marketing;
    if (settings.isApp && saveCookieConsentInApp) {
      storeCookieConsentInApp({
        hasConsented: true,
        isEnabled: true,
        wantsAnalysis: analysis,
        wantsMarketing,
        wantsPersonalization,
        wantsMarketingIsRedacted: hasConsentedBefore && !marketing,
        consentedDateTime: new Date().toISOString(),
      } as ICookieConsent);
    }
    fetch(`/c/api/v1/kj3ks/approve?a=${analysis}&m=${marketing}&p=${personalization}`)
      .then(() => {
        setOpen(false);
        if (!window.dataLayer) window.dataLayer = [];

        window.dataLayer.push({
          CookieConsent: {
            HasConsented: true,
            WantsAnalysis: analysis,
            WantsMarketing: wantsMarketing,
            WantsPersonalization: wantsPersonalization,
          },
        });
        googleConsentMode(wantsMarketing, analysis, "update", hasConsentedBefore);
        // GoogleConsentMode must be run before trackCookieConsentData,
        // else we don't get the analytics.
        trackCookieConsentData(wantsMarketing, analysis, wantsPersonalization);
        window.dispatchEvent(new CustomEvent("REFRESH_SETTINGS"));
      })
      .catch((e: any) => {
        logger.logError(`Approve Cookie Consent failed: ${e.message}`);
      });
  };

  if (forceShow == false && (consent?.hasConsented || hideInitialCookieConsentDialog)) {
    return null;
  }

  if (!consent) {
    return null;
  }
  return (
    <CookieConsentDialog
      strings={cookieStrings}
      consent={consent}
      approve={approveConsent}
      open={open}
      region={settings.region}
      disableCookieConsentForMarketingAndPersonalisation={settings.disableCookieConsentForMarketingAndPersonalisation}
    />
  );
};
export default CookieConsentDialogWrapper;
